import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useFindProductDesignOutputs,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductDesignOutput,
  BaseProductAssignDialogProps
} from '../Product.interface';
import ProductAutocomplete from '../components/ProductAutocomplete';
import { getProductEntityName } from '../Product.config';

export interface AssignDesignOutputDialogProps
  extends Omit<
    BaseProductAssignDialogProps<never, ProductDesignOutput>,
    'onAssigned' | 'productId'
  > {
  productId?: number;
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (designOutputs: ProductDesignOutput[]) => void;
}

const AssignDesignOutputDialog: React.FC<AssignDesignOutputDialogProps> = (
  props: AssignDesignOutputDialogProps
) => {
  const {
    open,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = `Assign ${getProductEntityName('designOutput')}`,
    contentText = `Search for ${getProductEntityName(
      'designOutput'
    )} to assign.`,
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [productId, setProductId] = useState(props.productId);
  const {
    productDesignOutputList,
    isLoadingProductDesignOutputs,
    loadingProductDesignOutputsError,
    findProductDesignOutputs
  } = useFindProductDesignOutputs(productId || 0);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignOutput
  );

  // handle searching
  useEffect(() => {
    const params: ApiParams = { search, page, limit };
    findProductDesignOutputs(params);
  }, [search, limit, page, productId, findProductDesignOutputs]);

  useEffect(() => {
    if (loadingProductDesignOutputsError) {
      onError?.(loadingProductDesignOutputsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductDesignOutputsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label={getProductEntityName('designOutput')}
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <Box mt={1} mb={2} className='search-box'>
          <ProductAutocomplete
            productId={productId}
            onChange={product => {
              if (product) {
                setProductId(product.id);
              }
            }}
          />
        </Box>
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: ProductDesignOutput) => prefix + item.id
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: ProductDesignOutput) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductDesignOutputs || isLoading}
          dataList={productDesignOutputList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductDesignOutputsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignDesignOutputDialog;
